import { chatService } from "../services/chatService";
import {
  BANDS_DIRECT_INTENT_STORAGE_KEY,
  BANDS_SELECTED_CHAT_STORAGE_KEY,
  BANDS_VIEW_MODE_STORAGE_KEY,
  BANDS_VIEW_MODE_WORKSPACE,
} from "../constants/bands";
import { CHAT_SELECTED_CHAT_STORAGE_KEY } from "../constants/chat";
import { BANDS_TAB_ID, CHAT_TAB_ID } from "../constants/MainNavigationTabSpecs";
import { ACTIVE_TAB_STORAGE_KEY, NAVIGATE_TAB_EVENT } from "../constants/navigation";
import { normalizeId, resolveDirectChatIdentity } from "./directChatIdentity";
import { registerUserScopedClientStateCleaner } from "../services/clientSessionLifecycle";

const directSessionInFlightByUserId = new Map();
registerUserScopedClientStateCleaner(() => directSessionInFlightByUserId.clear());

const normalizeChatId = (chat) => {
  const chatId = chat?.id ?? chat?.chatId;
  if (chatId === undefined || chatId === null) {
    return null;
  }
  const normalized = String(chatId).trim();
  return normalized || null;
};

const isDirectChatCandidate = (chat) => {
  if (!chat || typeof chat !== "object") {
    return false;
  }
  if (typeof chat.contextType === "string" && chat.contextType.trim().length > 0) {
    return chat.contextType.trim().toUpperCase() === "DIRECT";
  }
  return Array.isArray(chat.participantIds);
};

const resolveExistingDirectChat = (chats, friendUserId, currentUserId = null) => {
  if (!Array.isArray(chats) || chats.length === 0) {
    return null;
  }
  const normalizedFriendUserId = normalizeId(friendUserId);
  const normalizedCurrentUserId = normalizeId(currentUserId);
  if (!normalizedFriendUserId) {
    return null;
  }

  return chats.find((chat) => {
    if (!isDirectChatCandidate(chat)) {
      return false;
    }
    const participantIds = Array.isArray(chat.participantIds) ? chat.participantIds : [];
    if (normalizedCurrentUserId) {
      const identity = resolveDirectChatIdentity({
        participantIds,
        currentUserId: normalizedCurrentUserId,
      });
      return identity.userId === normalizedFriendUserId;
    }
    return participantIds.some((participantId) => normalizeId(participantId) === normalizedFriendUserId);
  }) || null;
};

const getOrCreateDirectChat = async (friendUserId, options = {}) => {
  const normalizedFriendUserId = normalizeId(friendUserId);
  if (!normalizedFriendUserId) {
    throw new Error("friendUserId is required");
  }

  const normalizedCurrentUserId = normalizeId(options.currentUserId);
  const requestKey = normalizedCurrentUserId
    ? `${normalizedCurrentUserId}:${normalizedFriendUserId}`
    : normalizedFriendUserId;
  if (directSessionInFlightByUserId.has(requestKey)) {
    return directSessionInFlightByUserId.get(requestKey);
  }

  const requestPromise = (async () => {
    let existingChat = null;
    try {
      const chats = await chatService.getMyChats();
      existingChat = resolveExistingDirectChat(
        chats,
        normalizedFriendUserId,
        normalizedCurrentUserId
      );
    } catch (listError) {
      console.warn("Unable to resolve existing direct chats before creation", listError);
    }

    const existingChatId = normalizeChatId(existingChat);
    if (existingChat && existingChatId) {
      return {
        chat: existingChat,
        chatId: existingChatId,
      };
    }

    const createdChat = await chatService.createDirectChat(normalizedFriendUserId);
    const createdChatId = normalizeChatId(createdChat);
    if (!createdChatId) {
      throw new Error("Missing chat id from response");
    }
    return {
      chat: createdChat,
      chatId: createdChatId,
    };
  })();

  directSessionInFlightByUserId.set(requestKey, requestPromise);
  try {
    return await requestPromise;
  } finally {
    if (directSessionInFlightByUserId.get(requestKey) === requestPromise) {
      directSessionInFlightByUserId.delete(requestKey);
    }
  }
};

export const navigateToDirectChat = (chatId) => {
  if (!chatId || typeof window === "undefined") {
    return;
  }

  const normalizedChatId = String(chatId);
  try {
    const payload = JSON.stringify({ type: "dm", directChatId: normalizedChatId });
    window.localStorage.setItem(
      BANDS_SELECTED_CHAT_STORAGE_KEY,
      payload
    );
    window.localStorage.setItem(BANDS_DIRECT_INTENT_STORAGE_KEY, payload);
    window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, BANDS_TAB_ID);
    window.sessionStorage.setItem(BANDS_VIEW_MODE_STORAGE_KEY, BANDS_VIEW_MODE_WORKSPACE);
  } catch (storageError) {
    console.warn("Unable to persist direct message selection", storageError);
  }

  window.dispatchEvent(
    new CustomEvent(NAVIGATE_TAB_EVENT, { detail: { tabId: BANDS_TAB_ID } })
  );
};

export const navigateToSeparateChat = (chatId) => {
  if (!chatId || typeof window === "undefined") {
    return;
  }

  const normalizedChatId = String(chatId);
  try {
    window.localStorage.setItem(CHAT_SELECTED_CHAT_STORAGE_KEY, normalizedChatId);
    window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, CHAT_TAB_ID);
  } catch (storageError) {
    console.warn("Unable to persist separate chat selection", storageError);
  }

  window.dispatchEvent(
    new CustomEvent(NAVIGATE_TAB_EVENT, {
      detail: { tabId: CHAT_TAB_ID, chatId: normalizedChatId },
    })
  );
};

export const startDirectMessageSession = async (friendUserId, options = {}) => {
  const { chat, chatId } = await getOrCreateDirectChat(friendUserId, options);

  navigateToSeparateChat(chatId);

  return {
    chat,
    chatId,
  };
};

export default startDirectMessageSession;
